"use client"

import { LeasingCarDto, SaleCarDto } from "api"
import { createContext, ReactNode, useContext, useState } from "react"

export type CarType = "sale" | "leasing"

interface ComparisonContextValue {
  cars: Map<string, SaleCarDto | LeasingCarDto>
  carType: CarType | null
  count: number
  canAddMore: boolean
  isFull: boolean
  addCar: (car: SaleCarDto | LeasingCarDto, type: CarType) => boolean
  removeCar: (carId: string) => void
  clearAll: () => void
  isCarSelected: (carId: string) => boolean
  canAddCarType: (type: CarType) => boolean
  getComparisonUrl: () => string | null
  getCarsArray: () => (SaleCarDto | LeasingCarDto)[]
}

const ComparisonContext = createContext<ComparisonContextValue | undefined>(
  undefined,
)

interface ComparisonState {
  cars: Map<string, SaleCarDto | LeasingCarDto>
  carType: CarType | null
}

export function ComparisonProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ComparisonState>({
    cars: new Map(),
    carType: null,
  })

  const { cars, carType } = state
  const count = cars.size
  const canAddMore = count < 3
  const isFull = count === 3

  const getCarId = (car: SaleCarDto | LeasingCarDto, type: CarType): string => {
    if (type === "sale") {
      return `sale-${(car as SaleCarDto).vehicleNumber}`
    } else {
      const leasingCar = car as LeasingCarDto
      return `leasing-${leasingCar.manufacturerCode}-${leasingCar.modelCode}`
    }
  }

  const canAddCarType = (type: CarType): boolean => {
    // If no cars, can add any type
    if (carType === null) return true
    // Otherwise, must match existing type
    return carType === type
  }

  const addCar = (car: SaleCarDto | LeasingCarDto, type: CarType): boolean => {
    // Check if we can add more
    if (isFull) return false

    const carId = getCarId(car, type)

    // Check if car is already selected
    if (cars.has(carId)) return false

    // If type mismatch, clear all and start fresh
    if (carType !== null && carType !== type) {
      setState({
        cars: new Map([[carId, car]]),
        carType: type,
      })
      return true
    }

    // Add car with type set atomically
    const newCars = new Map(cars)
    newCars.set(carId, car)
    setState({
      cars: newCars,
      carType: carType || type, // Set type if first car
    })

    return true
  }

  const removeCar = (carId: string): void => {
    const newCars = new Map(cars)
    newCars.delete(carId)

    // Update state atomically
    setState({
      cars: newCars,
      carType: newCars.size === 0 ? null : carType, // Reset type if no cars left
    })
  }

  const clearAll = (): void => {
    setState({
      cars: new Map(),
      carType: null,
    })
  }

  const isCarSelected = (carId: string): boolean => {
    return cars.has(carId)
  }

  const getCarsArray = (): (SaleCarDto | LeasingCarDto)[] => {
    return Array.from(cars.values())
  }

  const getComparisonUrl = (): string | null => {
    if (count === 0) return null

    if (carType === "leasing") {
      const leasingCarIds: string[] = []
      cars.forEach((car) => {
        const leasingCar = car as LeasingCarDto
        if (leasingCar?.manufacturerCode && leasingCar?.modelCode) {
          leasingCarIds.push(
            `${leasingCar.manufacturerCode}-${leasingCar.modelCode}`,
          )
        }
      })
      return `/comparison?type=leasing&cars=${leasingCarIds.join(",")}`
    } else {
      const saleCarIds: string[] = []
      cars.forEach((car) => {
        const saleCar = car as SaleCarDto
        if (saleCar?.vehicleNumber) {
          saleCarIds.push(String(saleCar.vehicleNumber))
        }
      })
      return `/comparison?type=sale&cars=${saleCarIds.join(",")}`
    }
  }

  const value: ComparisonContextValue = {
    cars,
    carType,
    count,
    canAddMore,
    isFull,
    addCar,
    removeCar,
    clearAll,
    isCarSelected,
    canAddCarType,
    getComparisonUrl,
    getCarsArray,
  }

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  )
}

export function useComparison(): ComparisonContextValue {
  const context = useContext(ComparisonContext)
  if (context === undefined) {
    throw new Error("useComparison must be used within a ComparisonProvider")
  }
  return context
}
